<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import WootButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  errorCodes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['reset']);
const { t } = useI18n();

const showDeliveryDropdown = ref(false);
const showReadDropdown = ref(false);
const showReplyDropdown = ref(false);
const showErrorDropdown = ref(false);

const KNOWN_ERROR_CODES = {
  // Meta / WhatsApp Cloud API Errors
  0: 'AuthException',
  3: 'API Method (Capability/Permissions)',
  10: 'Permission Denied',
  190: 'Access Token Expired',
  131000: 'Internal Error',
  131005: 'Access Denied',
  131008: 'Required Parameter Missing',
  131009: 'Parameter Value Invalid',
  131021: 'Recipient Cannot Be Sender',
  131026: 'Message Undeliverable (Invalid Recipient)',
  131042: 'Payment Eligibility Restricted',
  131045: 'Incorrect Certificate',
  131047: 'Re-engagement Window Expired',
  131048: 'Spam Rate Limit Hit',
  131049: 'Message Blocked (Ecosystem Protection)',
  131051: 'Unsupported Message Type',
  131056: 'Pair Rate Limit Hit',
  130429: 'Rate Limit Hit',
  368: 'Temporarily Blocked (Policy)',

  // Twilio Errors
  21211: 'Invalid Phone Number',
  21610: 'Unsubscribed Recipient',
  21612: 'Twilio: Service Unavailable',
  21614: 'Twilio: Mobile Number Not Reachable',
  30001: 'Twilio: Message Filtering',
  30002: 'Twilio: Account Suspended',
  30003: 'Twilio: Unreachable Destination',
  30004: 'Twilio: Message Blocked',
  30005: 'Twilio: Unknown Destination',
  30006: 'Twilio: Landline/Unreachable',
  30007: 'Twilio: Carrier Violation',
  30008: 'Twilio: Unknown Error',
  63003: 'Twilio: Component/Channel Error',
};

const deliveryStatusOptions = computed(() => [
  {
    value: '',
    label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.ALL'),
    action: 'select',
  },
  {
    value: 'pending',
    label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.PENDING'),
    action: 'select',
  },
  {
    value: 'sent',
    label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.SENT'),
    action: 'select',
  },
  {
    value: 'delivered',
    label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.DELIVERED'),
    action: 'select',
  },
  {
    value: 'failed',
    label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.FAILED'),
    action: 'select',
  },
]);

const readStatusOptions = computed(() => [
  {
    value: '',
    label: t('CAMPAIGN.DETAILS.FILTERS.READ.ALL'),
    action: 'select',
  },
  {
    value: 'read',
    label: t('CAMPAIGN.DETAILS.FILTERS.READ.READ'),
    action: 'select',
  },
  {
    value: 'unread',
    label: t('CAMPAIGN.DETAILS.FILTERS.READ.UNREAD'),
    action: 'select',
  },
]);

const errorCodeOptions = computed(() => {
  const codes = props.errorCodes || [];
  const validCodes = codes.filter(c => c !== null && c !== '');

  const options = validCodes.map(code => ({
    value: code,
    label: KNOWN_ERROR_CODES[code] || code,
    action: 'select',
  }));

  return [
    {
      value: '',
      label: t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.ALL'),
      action: 'select',
    },
    ...options,
  ];
});

const selectedErrorCodeLabel = computed(() => {
  const option = errorCodeOptions.value.find(
    opt => opt.value === props.modelValue.error_code
  );
  return option ? option.label : props.modelValue.error_code;
});

const selectedDeliveryStatusLabel = computed(() => {
  const option = deliveryStatusOptions.value.find(
    opt => opt.value === props.modelValue.delivery_status
  );
  return option?.label || t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.ALL');
});

const selectedReadStatusLabel = computed(() => {
  const option = readStatusOptions.value.find(
    opt => opt.value === props.modelValue.read_status
  );
  return option?.label || t('CAMPAIGN.DETAILS.FILTERS.READ.ALL');
});

const handleDeliveryStatusSelect = ({ value }) => {
  props.modelValue.delivery_status = value;
  showDeliveryDropdown.value = false;
};

const handleReadStatusSelect = ({ value }) => {
  props.modelValue.read_status = value;
  showReadDropdown.value = false;
};

const handleErrorCodeSelect = ({ value }) => {
  props.modelValue.error_code = value;
  showErrorDropdown.value = false;
};
</script>

<template>
  <div
    class="grid grid-cols-1 gap-4 p-4 mb-6 border rounded-xl border-n-alpha-black2 md:grid-cols-3"
  >
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.SEARCH') }}
      </label>
      <Input
        v-model="modelValue.q"
        type="search"
        :placeholder="t('CAMPAIGN.DETAILS.FILTERS.SEARCH_PLACEHOLDER')"
      />
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.DELIVERY.LABEL') }}
      </label>
      <div class="relative">
        <button
          class="w-full px-3 py-2 text-sm text-left border rounded-lg bg-n-alpha-black2 border-n-alpha-black3 hover:bg-n-alpha-3 flex items-center justify-between"
          @click="showDeliveryDropdown = !showDeliveryDropdown"
        >
          <span>{{ selectedDeliveryStatusLabel }}</span>
          <span class="i-lucide-chevron-down size-4" />
        </button>
        <DropdownMenu
          v-if="showDeliveryDropdown"
          v-on-clickaway="() => (showDeliveryDropdown = false)"
          :menu-items="deliveryStatusOptions"
          class="z-[100] w-full mt-2 top-full"
          @action="handleDeliveryStatusSelect"
        />
      </div>
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.READ.LABEL') }}
      </label>
      <div class="relative">
        <button
          class="w-full px-3 py-2 text-sm text-left border rounded-lg bg-n-alpha-black2 border-n-alpha-black3 hover:bg-n-alpha-3 flex items-center justify-between"
          @click="showReadDropdown = !showReadDropdown"
        >
          <span>{{ selectedReadStatusLabel }}</span>
          <span class="i-lucide-chevron-down size-4" />
        </button>
        <DropdownMenu
          v-if="showReadDropdown"
          v-on-clickaway="() => (showReadDropdown = false)"
          :menu-items="readStatusOptions"
          class="z-[100] w-full mt-2 top-full"
          @action="handleReadStatusSelect"
        />
      </div>
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.DELIVERED_RANGE') }}
      </label>
      <div class="flex gap-2">
        <Input v-model="modelValue.delivered_from" type="date" class="flex-1" />
        <Input v-model="modelValue.delivered_to" type="date" class="flex-1" />
      </div>
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.READ_RANGE') }}
      </label>
      <div class="flex gap-2">
        <Input v-model="modelValue.read_from" type="date" class="flex-1" />
        <Input v-model="modelValue.read_to" type="date" class="flex-1" />
      </div>
    </div>
    <div class="space-y-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DETAILS.FILTERS.ERROR_CODE') }}
      </label>
      <div class="relative">
        <button
          class="w-full px-3 py-2 text-sm text-left border rounded-lg bg-n-alpha-black2 border-n-alpha-black3 hover:bg-n-alpha-3 flex items-center justify-between"
          @click="showErrorDropdown = !showErrorDropdown"
        >
          <span class="truncate">{{
            selectedErrorCodeLabel ||
            t('CAMPAIGN.DETAILS.FILTERS.ERROR_PLACEHOLDER')
          }}</span>
          <span class="i-lucide-chevron-down size-4 flex-shrink-0" />
        </button>
        <DropdownMenu
          v-if="showErrorDropdown"
          v-on-clickaway="() => (showErrorDropdown = false)"
          :menu-items="errorCodeOptions"
          class="z-[100] w-full mt-2 top-full max-h-60 overflow-y-auto"
          @action="handleErrorCodeSelect"
        />
      </div>
    </div>
    <div class="flex items-end">
      <WootButton
        variant="ghost"
        size="sm"
        :label="t('CAMPAIGN.DETAILS.FILTERS.RESET')"
        @click="$emit('reset')"
      />
    </div>
  </div>
</template>
